import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const apiUrls = {
  forgot: `${API_BASE_URL}/user/resetPwd`,
  reset: `${API_BASE_URL}/user/changePwd`,
};

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get('mode') === 'reset' ? 'reset' : 'forgot';
  const [form, setForm] = useState({ userName: '', oldPassword: '', newPassword: '', resetCode: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      let body = { userName: form.userName };
      if (mode === 'reset') {
        if (form.oldPassword) body.oldPassword = form.oldPassword;
        if (form.newPassword) body.newPassword = form.newPassword;
        if (form.resetCode) body.resetCode = form.resetCode;
      }
      const response = await fetch(apiUrls[mode], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errorMsg = 'Failed to update password. Please check and try again.';
        try {
          const ct = response.headers.get('content-type');
          if (ct && ct.includes('application/json')) {
            const data = await response.json();
            if (data?.error || data?.message) errorMsg = data.error || data.message;
          } else {
            const text = await response.text();
            if (text) errorMsg = text;
          }
        } catch {}
        setError(errorMsg);
        return;
      }
      setSuccess(mode === 'reset' ? 'Reset code sent successfully!' : 'Password updated successfully!');
    } catch {
      setError('Failed to update password. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '8px',
    border: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
    background: '#fff', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.16s, box-shadow 0.16s', fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px',
  };

  const onFocus = e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; };
  const onBlur = e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; };

  return (
    <div className="login-container">
      <div className="login-box">
        <div style={{
          width: '48px', height: '48px', borderRadius: '13px',
          background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px', boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h1 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>
          {mode === 'reset' ? 'Password Management' : 'Forgot Password'}
        </h1>
        <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#64748b' }}>
          {mode === 'reset' ? 'Update or reset your password below.' : 'Enter your username to receive a reset code.'}
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && (
          <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: '8px', fontSize: '13px', fontWeight: '500', marginBottom: '14px' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label style={labelStyle}>Username</label>
            <input type="text" name="userName" value={form.userName} onChange={handleChange} placeholder="Enter your username" required style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          </div>

          {mode === 'reset' && (
            <>
              <div className="form-group">
                <label style={labelStyle}>New Password</label>
                <input type="password" name="newPassword" value={form.newPassword} onChange={handleChange} placeholder="Enter new password" required style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
              </div>
              <div className="form-group">
                <label style={labelStyle}>Current Password <span style={{ color: '#94a3b8', textTransform: 'none', letterSpacing: 0, fontSize: '11px', fontWeight: '400' }}>(voluntary change)</span></label>
                <input type="password" name="oldPassword" value={form.oldPassword} onChange={handleChange} placeholder="Enter current password" style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
              </div>
              <div className="form-group">
                <label style={labelStyle}>Reset Code <span style={{ color: '#94a3b8', textTransform: 'none', letterSpacing: 0, fontSize: '11px', fontWeight: '400' }}>(from forgot password)</span></label>
                <input type="text" name="resetCode" value={form.resetCode} onChange={handleChange} placeholder="Enter reset code" style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
              </div>
            </>
          )}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Submitting…' : mode === 'reset' ? 'Reset Password' : 'Send Reset Code'}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <span
            onClick={() => navigate('/on/login')}
            style={{ color: '#3b82f6', cursor: 'pointer', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}
            onMouseOver={e => e.target.style.textDecoration = 'underline'}
            onMouseOut={e => e.target.style.textDecoration = 'none'}
          >
            ← Back to Login
          </span>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
